import { Css, DnDGrid, DnDGridItemHandle, Icon, useDnDGridItem, useSnackbar, useTestIds } from "@homebound/beam";
import { ObjectConfig, useFormState } from "@homebound/form-state";
import { useRef } from "react";
import { queryResult } from "src/utils";
import {
  CommunityStageOrderDocument,
  CommunityStageOrderQuery,
  useCommunityStageOrderQuery,
  useSaveGlobalPlanStageVariantsMutation,
} from "./CommunityStageOrder.generated";

export function CommunityStageOrder({ communityId }: { communityId: string }) {
  const query = useCommunityStageOrderQuery({ variables: { communityId } });

  return queryResult(query, { data: (data) => <CommunityStageOrderView data={data} communityId={communityId} /> });
}

type StageOrderItem = {
  /** Stable client ID for form identity (same as globalPlanStageId) */
  id: string;
  globalPlanStageId: string;
  name: string;
  /** Order value from variant, undefined if no variant exists yet */
  order?: number;
  /** The id of the variant record if it exists */
  variantId?: string;
};

type FormState = { stages: StageOrderItem[] };

type CommunityStageOrderViewProps = { data: CommunityStageOrderQuery; communityId: string };

function CommunityStageOrderView({ data, communityId }: CommunityStageOrderViewProps) {
  const { triggerNotice } = useSnackbar();
  /** Normally we avoid using refetchQueries preferring to just use the mutation response to update the cache
   * However in this case, we we are blending two lists of data in this form in order to build variants of the global plan stages
   *  1. The full list of global plan stages
   *  2. The potentially-partial list of global plan stage variants which we can incrementally create/update in the form
   *  using the global plan stage ID as the stable identity key to convert the form state record after save without creating duplicates
   *  */
  const [saveVariants] = useSaveGlobalPlanStageVariantsMutation({
    refetchQueries: [CommunityStageOrderDocument],
    awaitRefetchQueries: true,
  });

  // Map existing variant orders by globalPlanStageId for quick lookup
  const variantsByStageId = data.community.globalPlanStageVariants.keyBy((v) => v.globalPlanStage.id);

  // Build initial stages list with a stable client ID for form identity
  const initialStages: StageOrderItem[] = data.globalPlanStages.map((stage) => {
    const variant = variantsByStageId[stage.id];
    return {
      id: stage.id, // Stable client ID for form identity
      globalPlanStageId: stage.id,
      name: stage.name,
      order: variant?.order ?? undefined,
      variantId: variant?.id,
    };
  });

  const formState = useFormState({
    config: formConfig,
    init: { input: { stages: initialStages }, map: (input) => input },
    autoSave: async ({ changedValue }) => {
      if (!changedValue.stages) return;

      // Only save if order actually changed
      const stagesWithOrderChanges = changedValue.stages.filter((s) => s.order !== undefined);
      if (stagesWithOrderChanges.isEmpty) return;

      const input = {
        globalPlanStageVariants: formState.stages.rows
          .filter((row) => row.order.value !== undefined)
          .map((row) => ({
            id: row.variantId.value || undefined,
            globalPlanStageId: row.globalPlanStageId.value,
            projectId: communityId,
            order: row.order.value,
          })),
      };
      if (input.globalPlanStageVariants.isEmpty) return;

      await saveVariants({ variables: { input } });
      triggerNotice({ message: "Stage order updated" });
    },
  });

  // Sort: ordered stages first (by order), then unordered (by name)
  const sortedStages = [
    ...formState.stages.rows.filter((r) => r.order.value !== undefined).sortBy((r) => r.order.value!),
    ...formState.stages.rows.filter((r) => r.order.value === undefined).sortBy((r) => r.name.value),
  ];

  const handleReorder = (newIds: string[]) => {
    // Batch all order updates into a single form state change to trigger one autosave
    const updatedStages = formState.stages.value.map((stage) => {
      const newIndex = newIds.indexOf(stage.id);
      return { ...stage, order: newIndex + 1 };
    });
    formState.stages.set(updatedStages);
  };

  return (
    <div css={Css.bgWhite.br8.mt2.bshBasic.p2.wPx(680).$}>
      <div css={Css.lg.mb2.$}>Stage Order For Community</div>
      <div css={Css.df.aic.gap1.sm.mb3.orange400.$}>
        <Icon icon="errorCircle" />
        <span>Warning: this order will update all project schedules in this community</span>
      </div>

      <DnDGrid onReorder={handleReorder} gridStyles={Css.dg.gtc("1fr").gap1.$}>
        {sortedStages.map((row) => (
          <StageChip key={row.id.value} stageId={row.id.value} name={row.name.value} order={row.order.value} />
        ))}
      </DnDGrid>
    </div>
  );
}

type StageChipProps = { stageId: string; name: string; order?: number };

function StageChip({ stageId, name, order }: StageChipProps) {
  const itemRef = useRef(null);
  const tid = useTestIds({}, "stageChip");
  const { dragHandleProps, dragItemProps } = useDnDGridItem({ id: stageId, itemRef });

  return (
    <div css={Css.df.aic.gap1.pyPx(6).pxPx(8).bgGray100.br4.$} ref={itemRef} {...dragItemProps} {...tid}>
      <DnDGridItemHandle icon="drag" dragHandleProps={dragHandleProps} compact />
      <span css={Css.xs.gray600.wPx(20).tac.$}>{order ?? "--"}</span>
      <span css={Css.xs.truncate.$} title={name}>
        {name}
      </span>
    </div>
  );
}

const formConfig: ObjectConfig<FormState> = {
  stages: {
    type: "list",
    config: {
      id: { type: "value" },
      globalPlanStageId: { type: "value" },
      name: { type: "value" },
      order: { type: "value" },
      variantId: { type: "value" },
    },
  },
};
